/**
 * Maps a panel slug (/<slug>) to its lazily-loaded component.
 * Each project is its own code-split chunk so the Portfolio bundle stays
 * lean, a chunk only loads when its case study is opened (or warmed on
 * arrow hover). The About panel is registered here too: it is not a
 * project (no Slide, no carousel entry) but it shares the slide-over
 * frame, entering from the left where case studies enter from the right.
 */
import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";
import type { Slide } from "../types";
import { SLIDES } from "../constants";
import { ASSET_TIER } from "../../../utils/assetTier";

/** Edge a panel enters from (and exits to). */
export type PanelSide = "left" | "right";

/** What CaseStudyOverlay needs to frame any slide-over page. */
export type Panel = {
    readonly slug: string;
    /** Accessible name of the region. */
    readonly label: string;
    /** Text/accent color; drives the global Nav/Footer while open. Unset =
     *  the site's dark text and the Nav/Footer's blend mode. */
    readonly textColor?: string;
    /** Explicit page background. Unset = faint auto-tint of textColor. */
    readonly bgColor?: string;
    readonly from: PanelSide;
};

/** Slug of the About panel (/about). */
export const ABOUT_SLUG = "about";

/** Every panel slug: the About panel and each project. App.tsx declares
 *  one child route of "/" per slug, so only these paths render Portfolio;
 *  any other path is the 404 (the office). */
export const PANEL_SLUGS: readonly string[] = [ABOUT_SLUG, ...SLIDES.map((s) => s.slug)];

/** A panel's address: /about, /<project>. */
export const getPanelPath = (slug: string) => `/${slug}`;

/** The panel slug a path addresses, or null for the bare carousel ("/")
 *  and for any path that is not a panel. */
export function getPanelSlugFromPath(path: string): string | null {
    const m = /^\/([^/]+)\/?$/.exec(path);
    return m && getPanelBySlug(m[1]) ? m[1] : null;
}

/** The carousel and its panels are one page (Portfolio stays mounted and
 *  the panel slides over it, with no curtain between): "/", "/about" and
 *  every "/<project>". */
export const isCarouselPath = (path: string) => path === "/" || getPanelSlugFromPath(path) !== null;

const ABOUT_PANEL: Panel = {
    slug: ABOUT_SLUG,
    label: "About",
    bgColor: "#FFFFFF",
    from: "left",
};

/** The About panel's component, loaded on open (or warmed on nav hover). */
export const AboutPanel = lazy(() => import("../AboutPanel"));

/** Framing for a slug: a case study (from a Slide) or the About panel. */
export function getPanelBySlug(slug: string): Panel | undefined {
    if (slug === ABOUT_SLUG) return ABOUT_PANEL;
    const s = getSlideBySlug(slug);
    if (!s) return undefined;
    return {
        slug,
        label: `${s.title} case study`,
        textColor: s.textColor,
        bgColor: s.bgColor,
        from: "right",
    };
}

export type CaseStudyComponent = ComponentType<{ slide: Slide }>;
type Loader = () => Promise<{ default: CaseStudyComponent }>;

const LOADERS: Record<string, Loader> = {
    "face-to-voice": () => import("./projects/FaceToVoice"),
    hdholdings: () => import("./projects/Hdholdings"),
    "the-pokemon-wall": () => import("./projects/ThePokemonWall"),
    halo: () => import("./projects/Halo"),
    "crypto-revolution": () => import("./projects/CryptoRevolution"),
    soquelec: () => import("./projects/Soquelec"),
    "soquelec-telecom": () => import("./projects/SoquelecTelecom"),
    "portfolio-site": () => import("./projects/PortfolioCase"),
};

const lazyCache = new Map<string, LazyExoticComponent<CaseStudyComponent>>();

/** Lazy component for a slug, or null if the slug is unknown. */
export function getCaseStudyComponent(slug: string): LazyExoticComponent<CaseStudyComponent> | null {
    const loader = LOADERS[slug];
    if (!loader) return null;
    let comp = lazyCache.get(slug);
    if (!comp) {
        comp = lazy(loader);
        lazyCache.set(slug, comp);
    }
    return comp;
}

export function getSlideBySlug(slug: string): Slide | undefined {
    return SLIDES.find((s) => s.slug === slug);
}

/** URLs whose preload `<img>` has fully loaded (bytes downloaded AND the
 *  browser has the decoded bitmap cached because the element is in the
 *  DOM). `CaseStudyImage` reads this synchronously to decide whether to
 *  skip its fade-in transition. Populated by the `HeroPreloader` component
 *  rendered by Portfolio. */
const prefetchLoaded = new Set<string>();

const imageLoadedListeners = new Set<(url: string) => void>();

/** Mark a hero URL as fully loaded. Called from the hidden preload imgs
 *  in Portfolio when their `onLoad` fires, and from a panel's own hero
 *  `<img>` once it has loaded. */
export function markImageLoaded(url: string): void {
    prefetchLoaded.add(url);
    imageLoadedListeners.forEach((listener) => listener(url));
}

/** Hear every `markImageLoaded`; returns the unsubscribe. */
export function onImageLoaded(listener: (url: string) => void): () => void {
    imageLoadedListeners.add(listener);
    return () => { imageLoadedListeners.delete(listener); };
}

/** Phones (the "sm" asset tier) get the small hero / poster bakes
 *  (`hero-sm.avif`, `*-poster-sm.avif`): the preloader keeps all of them
 *  decoded, and the full-size set is ~270 MB of bitmaps. Every consumer
 *  resolves through this, so the preload <img> and the panel request the
 *  same URL and share one decoded bitmap. Other URLs pass through. */
export function tierAssetUrl(url: string): string {
    if (ASSET_TIER !== "sm") return url;
    return url
        .replace(/\/hero\.avif$/, "/hero-sm.avif")
        .replace(/-poster\.avif$/, "-poster-sm.avif");
}

/** Build the canonical hero URL for a slug. Used by both the preload imgs
 *  in Portfolio and the registry's lookup. */
export function getHeroUrl(slug: string): string {
    return tierAssetUrl(`/assets/case-studies/${slug}/hero.avif`);
}

/** Canonical list of every case-study video poster URL. Generated by
 *  `npm run bake:posters` from each `clip-*.mp4` source file. Preloaded by
 *  Portfolio so the glow halo paints instantly when a case-study panel
 *  opens (instead of fading in from black during the slide-in). */
export const CASE_STUDY_POSTERS: readonly string[] = [
    "/assets/case-studies/crypto-revolution/clip-gallery-poster.avif",
    "/assets/case-studies/crypto-revolution/clip-inside-space-poster.avif",
    "/assets/case-studies/crypto-revolution/clip-vision-poster.avif",
    "/assets/case-studies/halo/clip-dissolve-comparison-poster.avif",
    "/assets/case-studies/halo/clip-loop-poster.avif",
    "/assets/case-studies/hdholdings/clip-entrance-poster.avif",
    "/assets/case-studies/hdholdings/clip-walkthrough-poster.avif",
    "/assets/case-studies/portfolio-site/clip-book-turn-poster.avif",
    "/assets/case-studies/portfolio-site/clip-carousel-poster.avif",
    "/assets/case-studies/portfolio-site/clip-index-loop-poster.avif",
    "/assets/case-studies/soquelec-telecom/clip-3d-logo-poster.avif",
    "/assets/case-studies/soquelec-telecom/clip-walkthrough-poster.avif",
    "/assets/case-studies/the-pokemon-wall/clip-browse-poster.avif",
    "/assets/case-studies/the-pokemon-wall/clip-open-poster.avif",
].map(tierAssetUrl);

/** True if the URL has been preloaded and the bitmap is cached. */
export function isImagePreloaded(url: string): boolean {
    return prefetchLoaded.has(url);
}

/** Warm a case-study chunk ahead of navigation (arrow hover/touchstart).
 *  The hero image is preloaded persistently by the `HeroPreloader` in
 *  Portfolio — this only needs to warm the JS chunk. */
export function prefetchCaseStudy(slug: string): void {
    LOADERS[slug]?.();
}
